import { useId, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useUpdateWorkspace } from '@/entities/collab';
import { surfaceProps, type WorkspaceAppearance } from '@/entities/appearance';
import { useWorkspaceFiles, useWorkspaceLinks, type Workspace } from '@/entities/workspace';
import { FormError } from '@/shared/forms';
import { Button, Icons, TextAreaField } from '@/shared/ui';

import styles from './WorkspaceInfo.module.css';
import { WorkspaceFiles } from './WorkspaceFiles';
import { WorkspaceLinks } from './WorkspaceLinks';

type Tab = 'about' | 'files' | 'links';
const DESCRIPTION_MAX = 2000;

/** The surface of the information area: independent of the header, with its own readable text. */
function Surface({
  appearance,
  preview = false,
  children,
}: {
  appearance: WorkspaceAppearance | null | undefined;
  preview?: boolean;
  children: React.ReactNode;
}) {
  const { t } = useTranslation();
  const about = surfaceProps(appearance?.description);
  return (
    <section
      className={styles.info}
      data-testid={preview ? undefined : 'workspace-description'}
      data-surface={about.custom ? 'custom' : 'none'}
      style={about.style}
      aria-label={t('workspaceHeader.info')}
    >
      {children}
    </section>
  );
}

/** The editor's static copy: only the description text on its surface. */
export function WorkspaceInfoPreview({
  text,
  appearance,
}: {
  text: string;
  appearance: WorkspaceAppearance | null | undefined;
}) {
  return (
    <Surface appearance={appearance} preview>
      <p className={`${styles.text} ${styles.clamped}`}>{text}</p>
    </Surface>
  );
}

/**
 * The information area under the header: the description and, when there are any (or the viewer
 * may add them), the workspace files and links. Tabs keep it one compact block that grows only
 * inside its own scrolling panels. Reading is for every member; ``canManage`` mirrors the server's
 * ``workspace.update_settings`` right (the server enforces it).
 */
export function WorkspaceInfo({
  workspace,
  canManage,
}: {
  workspace: Workspace;
  canManage: boolean;
}) {
  const { t } = useTranslation();
  const files = useWorkspaceFiles(workspace.id);
  const links = useWorkspaceLinks(workspace.id);
  const [tab, setTab] = useState<Tab>('about');
  const idBase = useId();
  const description = workspace.description?.trim() ?? '';
  const fileCount = files.data?.length ?? 0;
  const linkCount = links.data?.length ?? 0;

  const tabs: { id: Tab; label: string; count?: number }[] = [
    { id: 'about', label: t('workspaceHeader.tab.about') },
    ...(canManage || fileCount > 0
      ? [{ id: 'files' as const, label: t('workspaceHeader.tab.files'), count: fileCount }]
      : []),
    ...(canManage || linkCount > 0
      ? [{ id: 'links' as const, label: t('workspaceHeader.tab.links'), count: linkCount }]
      : []),
  ];
  // Nothing to say and nobody who could add something: no empty box at all.
  if (!description && tabs.length === 1 && !canManage) return null;
  const active = tabs.some((item) => item.id === tab) ? tab : 'about';

  return (
    <Surface appearance={workspace.appearance}>
      {tabs.length > 1 && (
        <div className={styles.tabs} role="tablist" aria-label={t('workspaceHeader.info')}>
          {tabs.map((item) => (
            <button
              key={item.id}
              type="button"
              role="tab"
              id={`${idBase}-tab-${item.id}`}
              aria-selected={active === item.id}
              aria-controls={`${idBase}-panel`}
              className={styles.tab}
              onClick={() => {
                setTab(item.id);
              }}
            >
              {item.label}
              {item.count ? <span className={styles.count}>{item.count}</span> : null}
            </button>
          ))}
        </div>
      )}
      <div
        className={styles.panel}
        role={tabs.length > 1 ? 'tabpanel' : undefined}
        id={`${idBase}-panel`}
        aria-labelledby={tabs.length > 1 ? `${idBase}-tab-${active}` : undefined}
      >
        {active === 'about' && (
          <Description workspace={workspace} text={description} canManage={canManage} />
        )}
        {active === 'files' && <WorkspaceFiles workspaceId={workspace.id} canManage={canManage} />}
        {active === 'links' && <WorkspaceLinks workspaceId={workspace.id} canManage={canManage} />}
      </div>
    </Surface>
  );
}

function Description({
  workspace,
  text,
  canManage,
}: {
  workspace: Workspace;
  text: string;
  canManage: boolean;
}) {
  const { t } = useTranslation();
  const update = useUpdateWorkspace(workspace.id);
  const body = useRef<HTMLParagraphElement>(null);
  const [open, setOpen] = useState(false);
  const [clipped, setClipped] = useState(false);
  const [draft, setDraft] = useState<string | null>(null);
  const [error, setError] = useState<unknown>(null);

  // Offer the toggle only when the clamped text really is cut off (re-measured on resize).
  useLayoutEffect(() => {
    const measure = () => {
      const element = body.current;
      if (element && !open) setClipped(element.scrollHeight > element.clientHeight + 1);
    };
    measure();
    window.addEventListener('resize', measure);
    return () => {
      window.removeEventListener('resize', measure);
    };
  }, [text, open, draft]);

  if (draft !== null) {
    return (
      <form
        className={styles.edit}
        onSubmit={(event) => {
          event.preventDefault();
          setError(null);
          update.mutate(
            { description: draft.trim() || null },
            {
              onSuccess: () => {
                setDraft(null);
              },
              onError: setError,
            },
          );
        }}
      >
        <TextAreaField
          label={t('workspaceHeader.description')}
          value={draft}
          rows={4}
          maxLength={DESCRIPTION_MAX}
          autoFocus
          onChange={(event) => {
            setDraft(event.target.value);
          }}
        />
        <FormError error={error} />
        <div className={styles.editActions}>
          <Button type="submit" variant="primary" size="sm" disabled={update.isPending}>
            {t('common.save')}
          </Button>
          <Button
            size="sm"
            onClick={() => {
              setDraft(null);
              setError(null);
            }}
          >
            {t('common.cancel')}
          </Button>
        </div>
      </form>
    );
  }

  return (
    <div className={styles.about}>
      {text ? (
        <p ref={body} className={`${styles.text} ${open ? '' : styles.clamped}`}>
          {text}
        </p>
      ) : (
        <p className={styles.empty}>{t('workspaceHeader.noDescription')}</p>
      )}
      <div className={styles.row}>
        {(clipped || open) && (
          <button
            type="button"
            className={styles.toggle}
            aria-expanded={open}
            onClick={() => {
              setOpen(!open);
            }}
          >
            {open ? t('workspaceHeader.less') : t('workspaceHeader.more')}
            <span className={open ? styles.up : undefined} aria-hidden>
              <Icons.chevronDown />
            </span>
          </button>
        )}
        {canManage && (
          <button
            type="button"
            className={styles.toggle}
            onClick={() => {
              setDraft(text);
            }}
          >
            <Icons.edit />
            {t(text ? 'workspaceHeader.editDescription' : 'workspaceHeader.addDescription')}
          </button>
        )}
      </div>
    </div>
  );
}
